import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { PagedFooter, Spinner } from '../../school_panel/components/ui'
import { usePagination } from '../../school_panel/lib/usePagination'
import { adminGetAllFields, adminGetAcademicYearCourses, adminGetFieldAcademicYears, adminGetUniversities } from '../../utils/remote'
import { fieldClass } from '../../school_panel/lib/fieldClass'

/** A YADA Education Hub university/college exam: one department, one course or several (mixed),
 *  for every university (the Hub's) or for one university, e.g. its final exam. */
export type UniPick = {
    fieldId: number | null
    fieldName: string
    yearId: number | null
    courses: { id: number; name: string; yearName: string }[]
    universityId: number | null
    universityName: string
}

export const HUB_NAME = 'YADA Education Hub'

export const EMPTY_UNI_PICK: UniPick = { fieldId: null, fieldName: '', yearId: null, courses: [], universityId: null, universityName: '' }

/** The name students see on the exam. */
export const uniOwnerName = (p: UniPick) => p.universityName || HUB_NAME

export const uniPickError = (p: UniPick) =>
    p.fieldId == null ? 'Choose a department to continue.'
    : p.courses.length === 0 ? 'Select at least one course.'
    : ''

/** "Programming" for one course, "Mixed · 3 courses" for several. */
export const uniCoursesText = (p: UniPick) =>
    p.courses.length === 1 ? p.courses[0].name
    : p.courses.length > 1 ? `Mixed · ${p.courses.length} courses`
    : ''

// Design `.opt` / `.opt.on`: pill options, navy fill when chosen.
const pillClass = (selected: boolean) =>
    `inline-flex min-h-[36px] max-w-full items-center gap-1.5 rounded-full border px-[13px] py-1 text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selected ? 'border-navy bg-navy text-white' : 'border-line-strong bg-surface text-ink hover:border-slate'}`

const LABEL = 'mb-1.5 block text-[13px] font-semibold text-ink'

function PagedPills<T>({ items, label, labelId, render, resetKey }: { items: T[]; label: string; labelId: string; render: (item: T) => React.ReactNode; resetKey?: unknown }) {
    const pg = usePagination(items, 10, resetKey)
    return (
        <div>
            <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-2">{pg.pageItems.map(render)}</div>
            <PagedFooter className="mt-3" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label={`${label} pagination`} />
        </div>
    )
}

const Loading = ({ what }: { what: string }) => <div className="flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> Loading {what}…</div>

/** Department → year → courses. Courses stay picked when the year changes, so one exam can mix years. */
export default function UniCoursePicker({ value, onChange, showErrors }: { value: UniPick; onChange: (v: UniPick) => void; showErrors?: boolean }) {
    const fields = useQuery({ queryKey: ['hub.uni.fields'], queryFn: adminGetAllFields, staleTime: 5 * 60_000 })
    const years = useQuery({
        queryKey: ['hub.uni.years', value.fieldId],
        queryFn: () => adminGetFieldAcademicYears(0, value.fieldId as number),
        enabled: value.fieldId != null,
    })
    const courses = useQuery({
        queryKey: ['hub.uni.courses', value.fieldId, value.yearId],
        queryFn: () => adminGetAcademicYearCourses(0, value.fieldId as number, value.yearId as number),
        enabled: value.fieldId != null && value.yearId != null,
    })
    const yearName = (years.data ?? []).find((y) => y.id === value.yearId)?.name ?? ''
    const sortedFields = [...(fields.data ?? [])].sort((a, b) => a.name.localeCompare(b.name))
    const error = uniPickError(value)

    const toggleCourse = (c: { id: number; name: string }) => {
        const on = value.courses.some((x) => x.id === c.id)
        onChange({ ...value, courses: on ? value.courses.filter((x) => x.id !== c.id) : [...value.courses, { id: c.id, name: c.name, yearName }] })
    }

    return (
        <div>
            <p id="uni-field-label" className={LABEL}>Department</p>
            {fields.isLoading ? <Loading what="departments" /> : fields.isError ? (
                <p className="text-[13px] text-danger">The departments could not be loaded. Reload the page to try again.</p>
            ) : (
                <PagedPills items={sortedFields} label="Department" labelId="uni-field-label" render={(f) => {
                    const selected = f.id === value.fieldId
                    return (
                        <button key={f.id} type="button" aria-pressed={selected} className={pillClass(selected)}
                            onClick={() => { if (!selected) onChange({ ...value, fieldId: f.id, fieldName: f.name, yearId: null, courses: [] }) }}>
                            <span className="line-clamp-2 break-words">{f.name}</span>
                        </button>
                    )
                }} />
            )}
            {showErrors && value.fieldId == null && <p className="mt-1.5 text-xs text-danger">{error}</p>}

            <p id="uni-year-label" className={`${LABEL} mt-5`}>Year</p>
            {value.fieldId == null ? <p className="text-[13px] text-muted">Choose a department to see its years.</p>
                : years.isLoading ? <Loading what="years" />
                : (years.data ?? []).length === 0 ? <p className="text-[13px] text-muted">This department has no years yet.</p>
                : (
                    <div role="group" aria-labelledby="uni-year-label" className="flex flex-wrap gap-2">
                        {(years.data ?? []).map((y) => (
                            <button key={y.id} type="button" aria-pressed={y.id === value.yearId} className={pillClass(y.id === value.yearId)}
                                onClick={() => onChange({ ...value, yearId: y.id })}>{y.name}</button>
                        ))}
                    </div>
                )}

            <p id="uni-course-label" className={`${LABEL} mt-5`}>Courses <span className="font-normal text-muted">· pick one, or several for a mixed exam</span></p>
            {value.yearId == null ? <p className="text-[13px] text-muted">Choose a year to see its courses.</p>
                : courses.isLoading ? <Loading what="courses" />
                : (courses.data ?? []).length === 0 ? <p className="text-[13px] text-muted">This year has no courses yet.</p>
                : (
                    <PagedPills items={courses.data ?? []} label="Courses" labelId="uni-course-label" resetKey={value.yearId} render={(c) => {
                        const selected = value.courses.some((x) => x.id === c.id)
                        return (
                            <button key={c.id} type="button" aria-pressed={selected} className={pillClass(selected)} onClick={() => toggleCourse(c)}>
                                <span className="line-clamp-2 break-words">{c.name}</span>
                            </button>
                        )
                    }} />
                )}
            {value.courses.length > 0 && (
                <div className="mt-3 rounded-[14px] border border-line bg-surface-2 px-3.5 py-2.5">
                    <p className="text-[12px] font-semibold text-muted">{value.courses.length === 1 ? 'Single-course exam' : `Mixed exam · ${value.courses.length} courses`}</p>
                    <ul className="m-0 mt-2 flex list-none flex-wrap gap-2 p-0">
                        {value.courses.map((c) => (
                            <li key={c.id} className="inline-flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 text-[12px] text-ink ring-1 ring-line">
                                {c.name}{c.yearName && <span className="text-muted"> · {c.yearName}</span>}
                                <button type="button" aria-label={`Remove ${c.name}`} onClick={() => toggleCourse(c)} className="ml-0.5 inline-flex rounded-full p-0.5 text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                    <X size={12} aria-hidden />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {showErrors && value.fieldId != null && value.courses.length === 0 && <p className="mt-1.5 text-xs text-danger">{error}</p>}

            <UniversityChoice id={value.universityId} name={value.universityName}
                onChange={(id, name) => onChange({ ...value, universityId: id, universityName: name })} />
        </div>
    )
}

/** Optional owner: every university under the Hub, or one university (it need not have an account here). */
export function UniversityChoice({ id, name, onChange }: { id: number | null; name: string; onChange: (id: number | null, name: string) => void }) {
    const unis = useQuery({ queryKey: ['hub.uni.universities'], queryFn: adminGetUniversities, staleTime: 5 * 60_000 })
    const [q, setQ] = React.useState('')
    const list = [...(unis.data ?? [])]
        .filter((u) => u.name.toLowerCase().includes(q.trim().toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name))
    return (
        <div className="mt-5">
            <p id="uni-owner-label" className={LABEL}>University <span className="font-normal text-muted">· optional, for one university's own exam</span></p>
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <button type="button" aria-pressed={id == null} className={pillClass(id == null)} onClick={() => onChange(null, '')}>
                    Any university · {HUB_NAME}
                </button>
                {id != null && (
                    <button type="button" aria-pressed className={pillClass(true)} onClick={() => onChange(null, '')} aria-label={`Remove ${name}`}>
                        {name}<X size={12} aria-hidden />
                    </button>
                )}
            </div>
            {unis.isLoading ? <Loading what="universities" /> : unis.isError ? (
                <p className="text-[13px] text-danger">The universities could not be loaded. Reload the page to try again.</p>
            ) : (
                <>
                    <input type="search" aria-label="Find a university" placeholder="Find a university" value={q} onChange={(e) => setQ(e.target.value)} className={`${fieldClass} mb-2.5`} />
                    {list.length === 0 ? <p className="text-[13px] text-muted">No university matches.</p> : (
                        <PagedPills items={list} label="Universities" labelId="uni-owner-label" resetKey={q} render={(u) => {
                            const selected = u.id === id
                            return (
                                <button key={u.id} type="button" aria-pressed={selected} className={pillClass(selected)} onClick={() => onChange(u.id, u.name)}>
                                    <span className="line-clamp-2 break-words">{u.name}</span>
                                </button>
                            )
                        }} />
                    )}
                </>
            )}
        </div>
    )
}
